import { makePrng } from '@learnforge/demo-math/numeric';

export type DistributionId = 'gaussian' | 'skewed-right' | 'bimodal' | 'uniform';

export interface DistributionSpec {
  id: DistributionId;
  params: Record<string, number>;
}

export function generatePopulation(spec: DistributionSpec, n: number, seed: number): number[] {
  const rand = makePrng(seed);
  const out = new Array<number>(n);
  switch (spec.id) {
    case 'gaussian': {
      const { mu = 0, sigma = 1 } = spec.params;
      for (let i = 0; i < n; i++) out[i] = mu + sigma * normalSample(rand);
      return out;
    }
    case 'uniform': {
      const { min = 0, max = 1 } = spec.params;
      for (let i = 0; i < n; i++) out[i] = min + rand() * (max - min);
      return out;
    }
    case 'skewed-right': {
      // log-normal shape; subtract a constant to keep the mode near `mode`.
      const { mode = 50, sigma = 0.5 } = spec.params;
      for (let i = 0; i < n; i++) {
        const z = normalSample(rand);
        out[i] = mode * Math.exp(sigma * z);
      }
      return out;
    }
    case 'bimodal': {
      const { mu1 = 30, mu2 = 70, sigma = 5, mix = 0.5 } = spec.params;
      for (let i = 0; i < n; i++) {
        const mu = rand() < mix ? mu1 : mu2;
        out[i] = mu + sigma * normalSample(rand);
      }
      return out;
    }
  }
}

/** Box-Muller normal sample. */
function normalSample(rand: () => number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = rand();
  while (v === 0) v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
